import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MapPin, Car, Clock, Landmark, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { getLocationBySlug } from '../data/locationsData';
import { getServiceBySlug } from '../data/servicesData';

const LocationPage = () => {
  const { locationSlug } = useParams();
  const location = getLocationBySlug(locationSlug);

  if (!location) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-stone-900 mb-3">Location not found</h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium"
            data-testid="location-not-found-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  const services = location.popularServices
    .map((slug) => getServiceBySlug(slug))
    .filter(Boolean);

  const path = `/locations/${location.slug}`;
  const canonical = `https://akirasignaturecare.com${path}`;

  // §3 SEO patterns from the implementation brief — programmatic templates
  // guarantee unique, intent-matched title + description on every city page.
  const seoTitle = `Medical Spa near ${location.name} | Akira Signature Care Alhambra`;
  const seoDescription = `Physician-led medical spa serving ${location.name} from Alhambra — Botox, fillers, microneedling, XERF & PRP with Angela Trias, M.D. Book (626) 737-3183.`;
  const seoH1 = `Medical Spa for ${location.name} Patients`;

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['MedicalBusiness', 'MedicalClinic'],
    '@id': `${canonical}#business`,
    name: 'Akira Signature Care',
    url: canonical,
    telephone: '+1-626-737-3183',
    priceRange: '$$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '506 N Garfield Ave Ste 210',
      addressLocality: 'Alhambra',
      addressRegion: 'CA',
      postalCode: '91801',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 34.0956,
      longitude: -118.1275,
    },
    areaServed: [
      { '@type': 'City', name: location.name },
      { '@type': 'AdministrativeArea', name: 'Los Angeles County, California' },
    ],
    medicalSpecialty: [
      'CosmeticProcedure',
      'PreventiveMedicine',
      'PrimaryCare',
    ],
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Locations', item: 'https://akirasignaturecare.com/locations' },
      { '@type': 'ListItem', position: 3, name: location.name, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`location-page-${location.slug}`}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={path}
        schema={[localBusinessSchema, breadcrumb]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white py-24">
        <div className="container mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors text-sm"
            data-testid="location-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-rose-300 text-sm mb-4">
              <MapPin className="w-4 h-4" />
              <span className="tracking-[0.3em] uppercase text-xs">{location.heroLabel}</span>
            </div>
            <h1
              className="font-serif text-4xl lg:text-5xl mb-5 leading-tight"
              data-testid="location-headline"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
            >
              {seoH1}
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed font-light">
              {location.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Getting here */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-stone-200 p-6" data-testid="location-drive-distance">
              <Car className="w-5 h-5 text-rose-500 mb-3" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-1.5">Distance</div>
              <div className="text-stone-900 font-medium">{location.driveInfo.distance}</div>
            </div>
            <div className="border border-stone-200 p-6" data-testid="location-drive-time">
              <Clock className="w-5 h-5 text-rose-500 mb-3" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-1.5">Drive time</div>
              <div className="text-stone-900 font-medium">{location.driveInfo.time}</div>
            </div>
            <div className="border border-stone-200 p-6" data-testid="location-directions">
              <MapPin className="w-5 h-5 text-rose-500 mb-3" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-1.5">Getting here</div>
              <div className="text-stone-900 font-medium text-sm">{location.driveInfo.exits}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Who we see + demographic */}
      <section className="py-20 bg-stone-50">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
              Who we see from {location.name}
            </span>
          </div>
          <h2 className="font-serif text-3xl text-stone-900 mb-6 leading-snug">
            Care built around this community
          </h2>
          <p className="text-stone-700 leading-relaxed text-lg font-light mb-6">
            {location.demographic}
          </p>
          {location.culturalNote && (
            <div className="border-l-4 border-rose-300 pl-5 py-2 bg-rose-50/40 text-stone-600 leading-relaxed text-base italic">
              {location.culturalNote}
            </div>
          )}
        </div>
      </section>

      {/* Popular services from this area */}
      {services.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-rose-400" />
              <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
                Popular with {location.name} patients
              </span>
            </div>
            <h2 className="font-serif text-3xl text-stone-900 mb-10 leading-snug">
              What we treat most from your area
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`location-service-${s.slug}`}
                  className="group block bg-white border border-stone-200 hover:border-rose-300 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500 ease-out shadow-sm overflow-hidden"
                >
                  {s.image && (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    {s.badge && (
                      <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-2">
                        {s.badge}
                      </div>
                    )}
                    <h3
                      className="font-serif text-lg text-stone-900 leading-snug group-hover:text-rose-500 transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {s.name}
                    </h3>
                    {s.tagline && (
                      <p className="text-stone-500 text-sm mt-2 font-light line-clamp-2">
                        {s.tagline}
                      </p>
                    )}
                    <div className="flex items-center gap-1 text-rose-500 text-xs mt-3">
                      Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Nearby landmarks (crawlable local context) */}
      <section className="py-16 bg-stone-100">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-4">
            <Landmark className="w-4 h-4 text-rose-500" />
            <span className="text-rose-500 text-[11px] tracking-[0.4em] uppercase font-light">
              Nearby {location.name} landmarks
            </span>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-stone-700 text-sm">
            {location.nearbyLandmarks.map((l) => (
              <li key={l} className="flex items-start gap-2">
                <span className="text-rose-400 mt-1">•</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <Sparkles className="w-8 h-8 text-rose-400 mx-auto mb-4" />
          <h2 className="font-serif text-3xl text-stone-900 mb-4 leading-snug">
            Ready to book from {location.name}?
          </h2>
          <p className="text-stone-600 font-light mb-8 leading-relaxed">
            Call us at <a className="text-rose-500 hover:text-rose-600" href="tel:+16267373183">(626) 737-3183</a>{' '}
            or book online for a consultation at our Alhambra clinic — 506 N Garfield Ave Ste 210, Alhambra, CA 91801.
          </p>
          <Link
            to="/contact"
            data-testid="location-cta-book"
            className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-medium tracking-widest text-xs uppercase px-8 py-3.5 transition-colors duration-300"
          >
            Book a consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
